import { useEffect, useState } from 'react';
import axios from 'axios';

// Manage payment integrations (M-Pesa paybills/tills, KCB, Airtel…) for one tenant,
// or for the platform itself when `tenant` is null.

const inputStyle = {
  width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.05)',
  border: '1.5px solid rgba(56,189,248,0.25)', borderRadius: 10, padding: '10px 14px',
  color: '#e2e8f0', fontSize: 15, outline: 'none', fontFamily: 'inherit',
};
const labelStyle = {
  display: 'block', color: '#e2e8f0', fontSize: 12.5, letterSpacing: 1.2,
  textTransform: 'uppercase', marginBottom: 6, fontWeight: 600,
};
const badge = (color) => ({
  background: `${color}1a`, border: `1px solid ${color}55`, borderRadius: 6,
  padding: '2px 8px', color, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
});
const btn = (color) => ({
  background: `${color}1a`, border: `1px solid ${color}40`, borderRadius: 8,
  padding: '6px 10px', color, fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
});

const emptyForm = (providerId = 'mpesa') => ({
  provider: providerId, label: '', environment: 'production',
  shortcode_type: 'paybill', shortcode: '', till_number: '',
  credentials: {}, is_default: false,
});

const CALLBACK_STATUS = {
  registered: ['#10b981', 'URLs registered'],
  failed: ['#f87171', 'Registration failed'],
  not_registered: ['#f59e0b', 'URLs not registered'],
};

export default function IntegrationsModal({ tenant, headers, onClose }) {
  const [providers, setProviders] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | integration
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState(null); // { ok, text }
  const [tenants, setTenants] = useState([]);  // for "Move to…"
  const [moveTo, setMoveTo] = useState({});    // integration id → chosen destination

  const tenantParam = tenant ? tenant.id : 'platform';
  const title = tenant ? tenant.name : 'Majic Water (platform / primary)';

  const load = async () => {
    try {
      const [p, i, t] = await Promise.all([
        axios.get('/api/integrations/providers', { headers }),
        axios.get('/api/integrations', { headers, params: { tenant_id: tenantParam } }),
        axios.get('/api/tenants', { headers }).catch(() => ({ data: { tenants: [] } })),
      ]);
      setProviders(p.data.providers);
      setItems(i.data.integrations);
      setTenants(t.data.tenants || []);
    } catch (err) {
      setNotice({ ok: false, text: err.response?.data?.message || 'Failed to load integrations' });
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tenantParam]);

  const providerOf = (id) => providers.find((p) => p.id === id);
  const formProvider = providerOf(form.provider);

  const startNew = () => {
    setForm(emptyForm('mpesa'));
    setEditing('new');
    setNotice(null);
  };

  const startEdit = (it) => {
    setForm({
      provider: it.provider, label: it.label, environment: it.environment,
      shortcode_type: it.shortcode_type || 'paybill', shortcode: it.shortcode || '',
      till_number: it.till_number || '', credentials: {}, is_default: it.is_default,
    });
    setEditing(it);
    setNotice(null);
  };

  const save = async () => {
    setBusy('save');
    setNotice(null);
    try {
      const body = { ...form, tenant_id: tenantParam };
      if (editing === 'new') {
        await axios.post('/api/integrations', body, { headers });
        setNotice({ ok: true, text: 'Integration added. Test it, then register its URLs with the provider.' });
      } else {
        await axios.put(`/api/integrations/${editing.id}`, body, { headers });
        setNotice({ ok: true, text: 'Integration updated' });
      }
      setEditing(null);
      await load();
    } catch (err) {
      setNotice({ ok: false, text: err.response?.data?.message || 'Save failed' });
    } finally {
      setBusy('');
    }
  };

  const action = async (it, kind) => {
    if (kind === 'register') {
      const warn = it.environment === 'production'
        ? '\n\nProduction shortcodes can usually only be registered once. Make sure the URLs below are final.'
        : '';
      if (!window.confirm(`Register these URLs with Safaricom for ${it.shortcode}?\n\n${it.callback_urls.confirmation}\n${it.callback_urls.validation}${warn}`)) return;
    }
    if (kind === 'deactivate' && !window.confirm(`Deactivate "${it.label}"? Payments will stop using it.`)) return;

    setBusy(`${kind}:${it.id}`);
    setNotice(null);
    try {
      let res;
      if (kind === 'test') res = await axios.post(`/api/integrations/${it.id}/test`, {}, { headers });
      if (kind === 'register') res = await axios.post(`/api/integrations/${it.id}/register-callbacks`, {}, { headers });
      if (kind === 'default') res = await axios.put(`/api/integrations/${it.id}`, { is_default: true }, { headers });
      if (kind === 'activate') res = await axios.put(`/api/integrations/${it.id}`, { active: true }, { headers });
      if (kind === 'deactivate') res = await axios.delete(`/api/integrations/${it.id}`, { headers });
      const ok = res.data.success !== false;
      setNotice({ ok, text: res.data.message || 'Done' });
    } catch (err) {
      setNotice({ ok: false, text: err.response?.data?.message || 'Request failed' });
    } finally {
      setBusy('');
      load();
    }
  };

  // Move a paybill/till to a tenant (or back to the platform). Its Safaricom URLs stay the same.
  const move = async (it) => {
    const dest = moveTo[it.id];
    if (!dest) return;
    const destTenant = dest === 'platform' ? null : tenants.find((t) => String(t.id) === dest);
    const where = destTenant ? `${destTenant.name} (${destTenant.subdomain})` : 'the platform (Majic Water)';
    if (!window.confirm(`Move "${it.label}" to ${where}?\n\nIt becomes that site's default ${it.provider === 'mpesa' ? 'M-Pesa paybill' : 'payment method'}, so its customers pay into it. Safaricom URLs don't need registering again.`)) return;
    setBusy(`move:${it.id}`);
    setNotice(null);
    try {
      await axios.post(`/api/integrations/${it.id}/assign`, { tenant_id: dest, make_default: true }, { headers });
      setNotice({ ok: true, text: `Moved "${it.label}" to ${where}` });
      setMoveTo((m) => ({ ...m, [it.id]: '' }));
    } catch (err) {
      setNotice({ ok: false, text: err.response?.data?.message || 'Could not move it' });
    } finally {
      setBusy('');
      load();
    }
  };

  const copy = (text) => navigator.clipboard?.writeText(text);

  return (
    <div className="mw-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 }}>
      <div className="mw-modal" style={{ background: '#0f172a', borderRadius: 16, border: '1px solid rgba(56,189,248,0.2)', padding: 28, width: '100%', maxWidth: 760, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h3 style={{ color: '#ffffff', fontSize: 20, fontWeight: 700, margin: 0 }}>Payment integrations</h3>
            <div style={{ color: '#cbd5e1', fontSize: 14.5, marginTop: 4 }}>{title}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#e2e8f0', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        {tenant?.use_primary_mpesa && (
          <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, padding: '10px 14px', color: '#fbbf24', fontSize: 14.5, marginBottom: 16 }}>
            This tenant currently collects M-Pesa on the Majic Water primary paybill. Adding a default M-Pesa integration here switches it to its own paybill/till.
          </div>
        )}

        {notice && (
          <div style={{ background: notice.ok ? 'rgba(16,185,129,0.1)' : 'rgba(248,113,113,0.1)', border: `1px solid ${notice.ok ? 'rgba(16,185,129,0.3)' : 'rgba(248,113,113,0.3)'}`, borderRadius: 10, padding: '10px 14px', color: notice.ok ? '#10b981' : '#f87171', fontSize: 14.5, marginBottom: 16, wordBreak: 'break-word' }}>
            {notice.text}
          </div>
        )}

        {loading ? (
          <p style={{ color: '#cbd5e1' }}>Loading…</p>
        ) : editing ? (
          /* ── Add / edit form ── */
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>Provider</label>
                <select value={form.provider} disabled={editing !== 'new'}
                  onChange={(e) => {
                    const p = providerOf(e.target.value);
                    setForm((f) => ({ ...f, provider: e.target.value, shortcode_type: p?.shortcodeTypes?.[0]?.value || '', credentials: {} }));
                  }} style={inputStyle}>
                  {providers.map((p) => (
                    <option key={p.id} value={p.id}>{p.label}{p.available ? '' : ' (coming soon)'}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Label</label>
                <input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="e.g. Nairobi paybill" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Environment</label>
                <select value={form.environment} onChange={(e) => setForm((f) => ({ ...f, environment: e.target.value }))} style={inputStyle}>
                  <option value="production">Production</option>
                  <option value="sandbox">Sandbox</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Type</label>
                <select value={form.shortcode_type} onChange={(e) => setForm((f) => ({ ...f, shortcode_type: e.target.value }))} style={inputStyle}>
                  {(formProvider?.shortcodeTypes || []).map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>{form.shortcode_type === 'till' ? 'Store / head office number' : 'Shortcode / paybill'}</label>
                <input value={form.shortcode} onChange={(e) => setForm((f) => ({ ...f, shortcode: e.target.value.trim() }))} placeholder="e.g. 174379" style={inputStyle} />
              </div>
              {form.shortcode_type === 'till' && (
                <div>
                  <label style={labelStyle}>Till number</label>
                  <input value={form.till_number} onChange={(e) => setForm((f) => ({ ...f, till_number: e.target.value.trim() }))} placeholder="Buy Goods till" style={inputStyle} />
                </div>
              )}
            </div>

            <div style={{ color: '#38bdf8', fontSize: 13.5, fontWeight: 700, letterSpacing: 1, margin: '8px 0 12px' }}>CREDENTIALS</div>
            {(formProvider?.credentialFields || []).map((f) => {
              const saved = editing !== 'new' ? editing.credentials?.[f.key] : null;
              return (
                <div key={f.key} style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>{f.label}{f.required && editing === 'new' ? ' *' : ''}</label>
                  <input type={f.secret ? 'password' : 'text'} autoComplete="new-password"
                    value={form.credentials[f.key] || ''}
                    onChange={(e) => setForm((fm) => ({ ...fm, credentials: { ...fm.credentials, [f.key]: e.target.value } }))}
                    placeholder={saved?.set ? `Saved ${saved.preview} — leave blank to keep` : ''}
                    style={inputStyle} />
                </div>
              );
            })}

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0 20px' }}>
              <input type="checkbox" id="is_default" checked={form.is_default} onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))} />
              <label htmlFor="is_default" style={{ color: '#e2e8f0', fontSize: 14.5 }}>Use as the default {formProvider?.label || ''} integration</label>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setEditing(null)} style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: 12, color: '#e2e8f0', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={save} disabled={busy === 'save' || !form.label} style={{ flex: 1, background: 'linear-gradient(135deg, #0891b2, #0e7490)', border: 'none', borderRadius: 10, padding: 12, color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: !form.label ? 0.5 : 1 }}>
                {busy === 'save' ? 'Saving…' : editing === 'new' ? 'Add integration' : 'Save changes'}
              </button>
            </div>
          </div>
        ) : (
          /* ── List ── */
          <div>
            {items.length === 0 && (
              <p style={{ color: '#cbd5e1', fontSize: 15 }}>
                No integrations yet.{!tenant && ' Payments use the primary M-Pesa credentials from the server .env until you add one.'}
              </p>
            )}

            {items.map((it) => {
              const p = providerOf(it.provider);
              const [cbColor, cbText] = CALLBACK_STATUS[it.callbacks_status] || CALLBACK_STATUS.not_registered;
              return (
                <div key={it.id} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(56,189,248,0.12)', borderRadius: 12, padding: 16, marginBottom: 12, opacity: it.active ? 1 : 0.55 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                    <div style={{ color: '#e2e8f0', fontWeight: 700 }}>{it.label}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <span style={badge('#38bdf8')}>{p?.label || it.provider}</span>
                      <span style={badge(it.environment === 'production' ? '#10b981' : '#f59e0b')}>{it.environment}</span>
                      {it.is_default && <span style={badge('#a78bfa')}>Default</span>}
                      {!it.active && <span style={badge('#f87171')}>Inactive</span>}
                    </div>
                  </div>

                  <div style={{ color: '#e2e8f0', fontSize: 14.5, marginBottom: 6 }}>
                    {it.shortcode_type === 'till' ? `Till ${it.till_number} (store ${it.shortcode})` : `${it.shortcode_type || ''} ${it.shortcode || ''}`}
                    {' · '}
                    {Object.entries(it.credentials || {}).map(([k, v]) => `${k}: ${v.set ? v.preview || 'set' : 'missing'}`).join(' · ')}
                  </div>

                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10, fontSize: 13.5 }}>
                    {p?.supportsCallbackRegistration && <span style={badge(cbColor)}>{cbText}</span>}
                    {it.last_tested_at && <span style={badge(it.last_test_ok ? '#10b981' : '#f87171')}>{it.last_test_ok ? 'Test passed' : 'Test failed'}</span>}
                    {it.credentials_error && <span style={badge('#f87171')}>Cannot decrypt credentials</span>}
                  </div>

                  {p?.supportsCallbackRegistration && (
                    <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: 8, padding: '8px 10px', marginBottom: 10, fontFamily: 'monospace', fontSize: 12.5, color: '#e2e8f0' }}>
                      {[['STK callback', it.callback_urls.stk], ['C2B confirm', it.callback_urls.confirmation], ['C2B validate', it.callback_urls.validation]].map(([k, url]) => (
                        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, wordBreak: 'break-all' }}>
                          <span><span style={{ color: '#cbd5e1' }}>{k}:</span> {url}</span>
                          <button onClick={() => copy(url)} title="Copy" style={{ background: 'none', border: 'none', color: '#38bdf8', cursor: 'pointer', fontSize: 12.5 }}>copy</button>
                        </div>
                      ))}
                      {it.callbacks_status === 'failed' && it.callbacks_last_response && (
                        <div style={{ color: '#f87171', marginTop: 6 }}>Last response: {it.callbacks_last_response}</div>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {it.active ? (
                      <>
                        <button style={btn('#38bdf8')} disabled={!!busy} onClick={() => action(it, 'test')}>{busy === `test:${it.id}` ? 'Testing…' : 'Test credentials'}</button>
                        {p?.supportsCallbackRegistration && (
                          <button style={btn('#10b981')} disabled={!!busy} onClick={() => action(it, 'register')}>
                            {busy === `register:${it.id}` ? 'Registering…' : it.callbacks_status === 'registered' ? 'Re-register URLs' : 'Register URLs with Safaricom'}
                          </button>
                        )}
                        {!it.is_default && <button style={btn('#a78bfa')} disabled={!!busy} onClick={() => action(it, 'default')}>Make default</button>}
                        <button style={btn('#e2e8f0')} disabled={!!busy} onClick={() => startEdit(it)}>Edit</button>
                        <button style={btn('#f87171')} disabled={!!busy} onClick={() => action(it, 'deactivate')}>Deactivate</button>
                        <div style={{ display: 'flex', gap: 6, flexBasis: '100%', marginTop: 4, flexWrap: 'wrap' }}>
                          <select aria-label={`Move ${it.label} to`} value={moveTo[it.id] || ''} onChange={(e) => setMoveTo((m) => ({ ...m, [it.id]: e.target.value }))}
                            style={{ ...inputStyle, width: 'auto', flex: '1 1 200px', padding: '7px 10px', fontSize: 14 }}>
                            <option value="">Move to another site…</option>
                            {tenant && <option value="platform">Platform (Majic Water main site)</option>}
                            {tenants.filter((t) => !tenant || t.id !== tenant.id).map((t) => (
                              <option key={t.id} value={t.id}>{t.name} ({t.subdomain}){t.active === false ? ' — inactive' : ''}</option>
                            ))}
                          </select>
                          <button style={btn('#fbbf24')} disabled={!!busy || !moveTo[it.id]} onClick={() => move(it)}>
                            {busy === `move:${it.id}` ? 'Moving…' : 'Move'}
                          </button>
                        </div>
                      </>
                    ) : (
                      <button style={btn('#10b981')} disabled={!!busy} onClick={() => action(it, 'activate')}>Reactivate</button>
                    )}
                  </div>
                </div>
              );
            })}

            <button onClick={startNew} style={{ width: '100%', marginTop: 8, background: 'rgba(56,189,248,0.1)', border: '1px dashed rgba(56,189,248,0.4)', borderRadius: 10, padding: 12, color: '#38bdf8', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
              + Add integration
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
